import { useState, useEffect, useRef } from 'react';

function AnimatedCounter({ target, duration = 1800 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return (
    <div ref={ref} className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-primary-500">
      {count.toLocaleString('ru-RU')}
    </div>
  );
}

interface Stat {
  label: string;
  value: number;
  years?: string;
}

function StatCard({ stat }: { stat: Stat }) {
  return (
    <div className="text-center">
      <AnimatedCounter target={stat.value} />
      <p className="mt-2 text-xs md:text-sm text-foreground-700 font-medium">{stat.label}</p>
      {stat.years && <p className="mt-0.5 text-xs text-foreground-500">{stat.years}</p>}
    </div>
  );
}

// Запасные значения (на случай, если /api/stats недоступен); живые приходят из API.
const FALLBACK = {
  documents: 127475,
  editions: 167696,
  law_years: { min: 1964, max: 2026 },
  courts: { ks: 84351, vs: 753770, district: 15888220, arbitration: 475061 },
  court_years: {
    ks: { min: 1990, max: 2026 },
    vs: { min: 1990, max: 2026 },
    district: { min: 2010, max: 2026 },
    arbitration: { min: 2017, max: 2026 },
  },
};

const range = (r?: { min?: number | null; max?: number | null }) =>
  r && r.min && r.max ? `${r.min}–${r.max}` : '';

export default function StatsBar() {
  // Живые значения из /api/stats (кэш на бэке 6 ч)
  const [d, setD] = useState<any>(FALLBACK);
  useEffect(() => {
    fetch('/api/stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => { if (v && v.decisions_total) setD(v); })
      .catch(() => {});
  }, []);

  const c = d.courts || FALLBACK.courts;
  const cy = d.court_years || FALLBACK.court_years;

  const legislation: Stat[] = [
    { label: 'Законов и НПА', value: d.documents ?? FALLBACK.documents, years: range(d.law_years) },
    { label: 'Редакций', value: d.editions ?? FALLBACK.editions },
  ];
  const courts: Stat[] = [
    { label: 'Конституционный Суд', value: c.ks, years: range(cy.ks) },
    { label: 'Верховный Суд', value: c.vs, years: range(cy.vs) },
    { label: 'Суды общей юрисдикции', value: c.district, years: range(cy.district) },
    { label: 'Арбитражные суды', value: c.arbitration, years: range(cy.arbitration) },
  ];

  return (
    <section className="w-full bg-background-100 border-y border-background-200/70">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <h2 className="text-center font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-1">
          База в цифрах
        </h2>
        <p className="text-center text-foreground-500 text-sm mb-7 md:mb-9">
          Что собрано на сегодня — законодательство и судебная практика
        </p>

        <p className="text-center text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-4">
          Законодательство
        </p>
        <div className="grid grid-cols-2 gap-6 md:gap-8 max-w-xl mx-auto mb-9">
          {legislation.map((s) => (
            <StatCard key={s.label} stat={s} />
          ))}
        </div>

        <p className="text-center text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-4">
          Судебная практика по инстанциям
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {courts.map((s) => (
            <StatCard key={s.label} stat={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
